import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './FCCompatibility.css'



const API = process.env.REACT_APP_API_URL || ""

function useCompatImages() {
    const [urls, setUrls] = useState({})
    useEffect(() => {
        fetch(`${API}/api/media/fc-compatibility`)
            .then(res => res.ok ? res.json() : {})
            .then(map => setUrls(map))
            .catch(() => { })
    }, [])
    return urls
}

/* ── SVG Fallbacks ────────────────────────────────────────────────────────── */

function EscFallback() {
    return (
        <svg viewBox="0 0 220 140" className="fcc-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="15" width="190" height="110" rx="4"
                fill="rgba(93,23,158,0.04)" stroke="rgba(93,23,158,0.3)" strokeWidth="1" />
            {[50, 90, 130, 170].map((x, i) => (
                <g key={i}>
                    <rect x={x - 8} y="8" width="16" height="10" rx="1"
                        fill="none" stroke="rgba(93,23,158,0.35)" strokeWidth="0.8" />
                    <rect x={x - 8} y="122" width="16" height="10" rx="1"
                        fill="none" stroke="rgba(93,23,158,0.35)" strokeWidth="0.8" />
                </g>
            ))}
            {[[40, 40], [135, 40], [40, 82], [135, 82]].map(([x, y], i) => (
                <rect key={i} x={x} y={y} width="46" height="30" rx="2"
                    fill="#fff" stroke="rgba(93,23,158,0.2)" strokeWidth="0.8" />
            ))}
            <rect x="82" y="57" width="56" height="22" rx="2"
                fill="rgba(93,23,158,0.08)" stroke="rgba(93,23,158,0.4)" strokeWidth="0.8" />
            <text x="110" y="72" textAnchor="middle"
                fill="#5d179e" fontSize="8" fontFamily="IBM Plex Mono" fontWeight="500">50Hz PWM</text>
        </svg>
    )
}

function MotorFallback() {
    return (
        <svg viewBox="0 0 220 140" className="fcc-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <circle cx="110" cy="70" r="58" fill="rgba(93,23,158,0.03)"
                stroke="rgba(93,23,158,0.2)" strokeWidth="1" strokeDasharray="5 4" />
            {Array.from({ length: 12 }, (_, i) => {
                const a = (i * 30 - 90) * Math.PI / 180
                return <line key={i}
                    x1={110 + 38 * Math.cos(a)} y1={70 + 38 * Math.sin(a)}
                    x2={110 + 56 * Math.cos(a)} y2={70 + 56 * Math.sin(a)}
                    stroke="rgba(93,23,158,0.3)" strokeWidth="3" strokeLinecap="round" />
            })}
            <circle cx="110" cy="70" r="34" fill="rgba(93,23,158,0.05)"
                stroke="rgba(93,23,158,0.3)" strokeWidth="1" />
            <circle cx="110" cy="70" r="10" fill="#f5f2ee"
                stroke="rgba(93,23,158,0.4)" strokeWidth="1" />
            <circle cx="110" cy="70" r="4" fill="rgba(93,23,158,0.4)" />
            <text x="110" y="68" textAnchor="middle"
                fill="rgba(93,23,158,0.5)" fontSize="7" fontFamily="IBM Plex Mono">ALL KV</text>
            <text x="110" y="78" textAnchor="middle"
                fill="rgba(93,23,158,0.3)" fontSize="6" fontFamily="IBM Plex Mono">1104–2212</text>
        </svg>
    )
}

function LipoFallback() {
    return (
        <svg viewBox="0 0 220 140" className="fcc-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="30" width="168" height="80" rx="6"
                fill="rgba(93,23,158,0.04)" stroke="rgba(93,23,158,0.3)" strokeWidth="1" />
            <rect x="188" y="52" width="12" height="36" rx="2"
                fill="rgba(93,23,158,0.08)" stroke="rgba(93,23,158,0.3)" strokeWidth="0.8" />
            <line x1="76" y1="30" x2="76" y2="110" stroke="rgba(93,23,158,0.15)" strokeWidth="0.8" />
            <line x1="132" y1="30" x2="132" y2="110" stroke="rgba(93,23,158,0.15)" strokeWidth="0.8" />
            {[{ x: 28, pct: 0.75 }, { x: 84, pct: 0.88 }, { x: 140, pct: 0.6 }].map(({ x, pct }, i) => (
                <g key={i}>
                    <rect x={x} y={38} width={40} height={64} rx="2" fill="rgba(93,23,158,0.03)" />
                    <rect x={x} y={38 + 64 * (1 - pct)} width={40} height={64 * pct} rx="2"
                        fill="rgba(93,23,158,0.12)" />
                </g>
            ))}
            <text x="104" y="22" textAnchor="middle"
                fill="rgba(93,23,158,0.5)" fontSize="8" fontFamily="IBM Plex Mono"
                letterSpacing="3">2S – 4S LiPo</text>
            <rect x="28" y="46" width="36" height="16" rx="1"
                fill="rgba(93,23,158,0.12)" stroke="rgba(93,23,158,0.3)" strokeWidth="0.7" />
            <text x="46" y="57" textAnchor="middle"
                fill="#5d179e" fontSize="7" fontFamily="IBM Plex Mono" fontWeight="500">5V REG</text>
        </svg>
    )
}

function StemFallback() {
    return (
        <svg viewBox="0 0 220 140" className="fcc-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <rect x="30" y="10" width="160" height="100" rx="4"
                fill="rgba(93,23,158,0.04)" stroke="rgba(93,23,158,0.3)" strokeWidth="1" />
            <rect x="20" y="110" width="180" height="8" rx="2" fill="rgba(13,10,24,0.06)" />
            {[
                ['GYRO_X', '+0.12°', 44],
                ['GYRO_Y', '-0.03°', 60],
                ['PWM_1 ', '1480μs', 76],
                ['ALT   ', '12.4 m', 92],
            ].map(([lbl, val, y]) => (
                <g key={y}>
                    <text x="42" y={y} fill="rgba(93,23,158,0.35)"
                        fontSize="8" fontFamily="IBM Plex Mono">{'>'} {lbl}</text>
                    <text x="120" y={y} fill="rgba(93,23,158,0.7)"
                        fontSize="8" fontFamily="IBM Plex Mono">{val}</text>
                </g>
            ))}
            <rect x="42" y="98" width="4" height="8" rx="1"
                fill="rgba(93,23,158,0.6)" />
            <rect x="148" y="76" width="30" height="18" rx="2"
                fill="rgba(93,23,158,0.08)" stroke="rgba(93,23,158,0.25)" strokeWidth="0.7" />
            <text x="163" y="88" textAnchor="middle"
                fill="#5d179e" fontSize="7" fontFamily="IBM Plex Mono">RTF</text>
        </svg>
    )
}

/* ── Visual slot — image if loaded, fallback SVG otherwise ───────────────── */
function VisualSlot({ imgUrl, Fallback, alt }) {
    if (imgUrl && imgUrl !== 'error') {
        return (
            <div className="fcc-img-slot">
                <img src={imgUrl} alt={alt} className="fcc-img" />
            </div>
        )
    }
    return (
        <div className="fcc-img-slot fcc-img-slot--fallback">
            <Fallback />
        </div>
    )
}

/* ── Single timeline row ─────────────────────────────────────────────────── */
function TimelineRow({ data, index, imgUrl }) {
    const rowRef = useRef(null)
    const isEven = index % 2 === 0

    useEffect(() => {
        gsap.fromTo(rowRef.current,
            { opacity: 0, y: 40 },
            {
                opacity: 1, y: 0, duration: 0.85, ease: 'power3.out',
                delay: index * 0.13,
                scrollTrigger: { trigger: rowRef.current, start: 'top 88%', toggleActions: 'play none none none' },
            }
        )
    }, [index])

    const textBlock = (
        <div className="fcc-tl-text">
            <span className="fcc-tl-tag"
                style={{ color: data.accent, borderColor: `rgba(${data.rgb},0.3)` }}>
                {data.tag}
            </span>
            <h3 className="fcc-tl-name">{data.name}</h3>
            <p className="fcc-tl-tagline" style={{ color: data.accent }}>{data.tagline}</p>
            <p className="fcc-tl-desc">{data.desc}</p>
            <div className="fcc-tl-badges">
                {data.badges.map((b, i) => (
                    <span key={i} className="fcc-badge">{b}</span>
                ))}
            </div>
        </div>
    )

    const visual = (
        <div className="fcc-tl-visual">
            <VisualSlot imgUrl={imgUrl} Fallback={data.Fallback} alt={data.name} />
        </div>
    )

    return (
        <div ref={rowRef} className={`fcc-tl-row ${isEven ? 'fcc-tl-row--even' : 'fcc-tl-row--odd'}`}
            style={{ opacity: 0 }}>
            {isEven ? (
                <>
                    <div className="fcc-tl-side fcc-tl-side--left">{textBlock}</div>
                    <div className="fcc-tl-spine">
                        <div className="fcc-tl-num">{data.index}</div>
                        <div className="fcc-tl-dot" style={{ background: data.accent }} />
                        <div className="fcc-tl-line" />
                    </div>
                    <div className="fcc-tl-side fcc-tl-side--right">{visual}</div>
                </>
            ) : (
                <>
                    <div className="fcc-tl-side fcc-tl-side--left">{visual}</div>
                    <div className="fcc-tl-spine">
                        <div className="fcc-tl-num">{data.index}</div>
                        <div className="fcc-tl-dot" style={{ background: data.accent }} />
                        <div className="fcc-tl-line" />
                    </div>
                    <div className="fcc-tl-side fcc-tl-side--right">{textBlock}</div>
                </>
            )}
        </div>
    )
}

/* ── Data ────────────────────────────────────────────────────────────────── */
const ITEMS = [
    {
        id: 'esc', index: '01', tag: 'HARDWARE',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcc-esc',
        name: 'Standard ESCs',
        tagline: 'Any ESC. Any brand. Zero lock-in.',
        desc: 'Works out of the box with every standard ESC over 50 Hz PWM. No proprietary protocol, no special firmware — solder, connect, arm.',
        badges: ['PWM Signal', 'Any Brand', '50 Hz'],
        Fallback: EscFallback,
    },
    {
        id: 'bldc', index: '02', tag: 'HARDWARE',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcc-motor',
        name: 'BLDC Motors',
        tagline: 'Four axes. Any KV. Full throttle control.',
        desc: 'Four dedicated PWM outputs drive brushless motors from 1104 up to 2212 class. All KV ranges, any prop — precise per-axis throttle every time.',
        badges: ['4× PWM Out', '1104 – 2212', 'All KV'],
        Fallback: MotorFallback,
    },
    {
        id: 'lipo', index: '03', tag: 'POWER',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcc-lipo',
        name: 'LiPo Batteries',
        tagline: '2S to 4S. Clean 5V. Always stable.',
        desc: '2S–4S LiPo recommended. The onboard 5V regulator keeps the FC clean regardless of battery sag, temperature, or flight load.',
        badges: ['2S – 4S LiPo', '5V Regulated', 'Stable Power'],
        Fallback: LipoFallback,
    },
    {
        id: 'stem', index: '04', tag: 'EDUCATION',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcc-stem',
        name: 'STEM Education',
        tagline: 'Beginner safe. Classroom ready.',
        desc: 'Beginner-safe flight tuning with no PID setup required. Ships ready to fly — ideal for classrooms, workshops, hackathons and RPTO training batches.',
        badges: ['No PID Needed', 'Ready To Fly', 'RPTO Ready'],
        Fallback: StemFallback,
    },
]

/* ── Section ─────────────────────────────────────────────────────────────── */
export default function FCCompatibility() {
    const wrapRef = useRef(null)
    const eyebrowRef = useRef(null)
    const h2Ref = useRef(null)
    const subRef = useRef(null)
    const urls = useCompatImages()

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(eyebrowRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
                    scrollTrigger: { trigger: eyebrowRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(h2Ref.current, { opacity: 0, y: 60 },
                {
                    opacity: 1, y: 0, duration: 1.1, ease: 'expo.out',
                    scrollTrigger: { trigger: h2Ref.current, start: 'top 85%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(subRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: 'expo.out',
                    scrollTrigger: { trigger: subRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} className="fcc-wrap">
            <div className="fcc-bg-grid" />
            <div className="fcc-inner">

                <div className="fcc-header">
                    <div ref={eyebrowRef} className="fcc-eyebrow" style={{ opacity: 0 }}>
                        <span className="fcc-dot" />COMPATIBILITY
                    </div>
                    <h2 ref={h2Ref} className="fcc-h2" style={{ opacity: 0 }}>
                        Plug In.<br /><em>Just Fly.</em>
                    </h2>
                    <p ref={subRef} className="fcc-sub" style={{ opacity: 0 }}>
                        MockFly speaks the language every drone builder already knows — standard
                        ESCs, any BLDC motor, a LiPo from your drawer. Zero proprietary lock-in.
                    </p>
                </div>

                <div className="fcc-timeline">
                    {ITEMS.map((item, i) => (
                        <TimelineRow
                            key={item.id}
                            data={item}
                            index={i}
                            imgUrl={urls[item.imgKey]}
                        />
                    ))}
                </div>

            </div>
        </section>
    )
}